import { action, observable } from 'mobx';
import * as IoC from 'typescript-ioc';
import { AbstractFormViewModelController } from '../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { FormVmItemConfigType, FormVmValueType } from '../../../../../../infrastructure/form-viewmodel-controller';
import { CustomPageFormField } from '../../../../../../modules/passport-pages/custom-pages';
import CONFIG from '../../../../../../modules/event-links/config';
import {IEventLinkDTO, IEventLinkType} from '../../../../../../api/event-links.dao';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';

type EventLinkProps = {
    types: IEventLinkType[];
    data?: IEventLinkDTO;
};

export class EventLinkFormViewModel extends AbstractFormViewModelController {
    public static create(formProps: EventLinkProps, data?: IEventLinkDTO): EventLinkFormViewModel {
        const {types, ...rest} = formProps;
        return new EventLinkFormViewModel(types, data, rest as EventLinkProps, CONFIG);
    }

    @action.bound
    public setFieldValueByKey(key: string, value: FormVmValueType): void {
        if (this.data) this.data[key] = value;
        this.setValue(key, value);
    }
    public getFieldByKey(key: string): CustomPageFormField {
        if (this.data?.[key]) this.setValue(key, this.data?.[key]);
        return {
            value: this.data?.[key] ?? this.getValue(key),
            ...this.getValueValidationState(key)
        };
    }
    public getFieldConfigByKey(key: string): FormVmItemConfigType {
        return this.config.find(cnf => cnf.name === key);
    }

    @action.bound
    public setLinkType(value: string): void {
        if (this.data) this.data.type = value;
        this.linkType = value;
    }

    public getLinkType(): string {
        return this.data?.type ?? (this.linkType || this.defaultLinkType);
    }

    public get linkTypeOptions(): string[] {
        return this.types.map(({value}) => value);
    }

    public getFormFields() {
        return this.getVmState()
            .reduce((fields, field) => {
                fields[field.name] = field.value;
                return fields;
            }, {type: this.getLinkType()});
    }

    @action.bound
    public reset(): void {
        this.resetVm();
        this.resetLinkType();
    }

    constructor(
        private readonly types: IEventLinkType[],
        private readonly data: IEventLinkDTO,
        props: EventLinkProps,
        private readonly config: FormVmItemConfigType[],
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider)
    ) {
        super(props, 'EventLink', config);
    }

    private get defaultLinkType(): string {
        return this.types.find(type => type.default)?.value || this.types[0]?.value || '';
    }

    @action
    private resetLinkType(): void {
        this.linkType = null;
    }
    @observable
    private linkType: string | null;
}
